// config/utils.ts
import { type Area } from "react-easy-crop";
import {
  generateUploadButton,
  generateUploadDropzone,
  generateReactHelpers,
} from "@uploadthing/react";
import type { OurFileRouter } from "@uploadthing/core";
import { AVATAR_CONFIG } from "./constants";
import { FileValidationResult } from "./tipos";
import { useUserStore } from "store";

// ==========================================
// UPLOADTHING COMPONENTS & HELPERS
// ==========================================

export const UploadButton = generateUploadButton<OurFileRouter>();
export const UploadDropzone = generateUploadDropzone<OurFileRouter>();
export const { useUploadThing, uploadFiles } =
  generateReactHelpers<OurFileRouter>();

// ==========================================
// IMAGE PROCESSING UTILITIES
// ==========================================

/**
 * Recorta uma imagem com base na área especificada usando react-easy-crop
 * @param file - Arquivo de imagem original
 * @param cropArea - Área de recorte definida pelo react-easy-crop
 * @returns Promise com o arquivo recortado em formato PNG
 */
export function getCroppedImage(file: File, cropArea: Area): Promise<File> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = cropArea.width;
        canvas.height = cropArea.height;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
          return reject(new Error("Erro ao obter contexto do canvas"));
        }

        // Desenha a área recortada no canvas
        ctx.drawImage(
          img,
          cropArea.x,
          cropArea.y,
          cropArea.width,
          cropArea.height,
          0,
          0,
          cropArea.width,
          cropArea.height
        );

        canvas.toBlob((blob) => {
          if (!blob) {
            return reject(new Error("Erro ao converter imagem recortada"));
          }

          const croppedFile = new File([blob], file.name, {
            type: "image/png",
          });
          resolve(croppedFile);
        }, "image/png");
      };

      img.onerror = () =>
        reject(new Error("Erro ao carregar imagem para recorte"));
      img.src = reader.result as string;
    };

    reader.onerror = () =>
      reject(new Error("Erro ao ler arquivo para recorte"));
    reader.readAsDataURL(file);
  });
}

/**
 * Converte qualquer imagem para formato PNG
 * @param file - Arquivo de imagem a ser convertido
 * @returns Promise com o arquivo convertido para PNG
 */
export async function convertImageToPng(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
          return reject(new Error("Canvas context inválido"));
        }

        ctx.drawImage(img, 0, 0);

        canvas.toBlob((blob) => {
          if (!blob) {
            return reject(new Error("Erro ao criar PNG"));
          }

          const pngFile = new File(
            [blob],
            file.name.replace(/\.\w+$/, ".png"),
            {
              type: "image/png",
              lastModified: Date.now(),
            }
          );

          resolve(pngFile);
        }, "image/png");
      };

      img.onerror = () => reject(new Error("Erro ao carregar imagem"));
      img.src = reader.result as string;
    };

    reader.onerror = () => reject(new Error("Erro ao ler arquivo"));
    reader.readAsDataURL(file);
  });
}

// ==========================================
// DRAG & DROP UTILITIES
// ==========================================

/**
 * Cria handlers para drag and drop que podem ser usados em componentes
 * @param onDrop - Callback executado quando um arquivo é solto
 * @param setIsDragging - Setter para controlar o estado de dragging (opcional)
 * @returns Objeto com os handlers de drag and drop
 */
export function createDragAndDropHandlers(
  onDrop: (file: File) => void,
  setIsDragging?: (isDragging: boolean) => void
) {
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging?.(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging?.(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging?.(false);

    const file = event.dataTransfer.files?.[0];
    if (file) {
      onDrop(file);
    }
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return {
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
    onDragEnter: handleDragEnter,
  };
}

// ==========================================
// FILE VALIDATION UTILITIES
// ==========================================

/**
 * Valida se o arquivo é uma imagem válida
 * @param file - Arquivo a ser validado
 * @returns true se for uma imagem válida
 */
export function isValidImage(file: File): boolean {
  const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
  return validTypes.includes(file.type);
}

/**
 * Valida o tamanho do arquivo
 * @param file - Arquivo a ser validado
 * @param maxSizeInMB - Tamanho máximo em MB (padrão: 4MB)
 * @returns true se o arquivo estiver dentro do limite
 */
export function isValidFileSize(file: File, maxSizeInMB: number = 4): boolean {
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
  return file.size <= maxSizeInBytes;
}

/**
 * Valida arquivo de imagem completo (tipo e tamanho)
 * @param file - Arquivo a ser validado
 * @param maxSizeInMB - Tamanho máximo em MB (padrão: 4MB)
 * @returns Objeto com resultado da validação e mensagem de erro se houver
 */
export function validateImageFile(
  file: File,
  maxSizeInMB: number = 4
): FileValidationResult {
  if (!isValidImage(file)) {
    return {
      isValid: false,
      error: "Formato de arquivo não suportado. Use JPEG, PNG ou WebP.",
    };
  }

  if (!isValidFileSize(file, maxSizeInMB)) {
    return {
      isValid: false,
      error: `Arquivo muito grande. Tamanho máximo: ${maxSizeInMB}MB.`,
    };
  }

  return { isValid: true };
}

// ==========================================
// AVATAR SPECIFIC UTILITIES
// ==========================================

/**
 * Valida arquivo especificamente para avatar
 */
export function validateAvatarFile(file: File): FileValidationResult {
  const validation = validateImageFile(file, AVATAR_CONFIG.MAX_FILE_SIZE_MB);
  return validation;
}

/**
 * Utilitário para obter o usuário atual do Zustand.
 */
export function getCurrentUser() {
  return useUserStore.getState().user;
}

/**
 * Processa crop do avatar e prepara para upload
 */
export async function processAvatarCrop(
  selectedFile: File,
  croppedAreaPixels: Area
): Promise<File> {
  const user = getCurrentUser();
  if (!user?.login) {
    console.log(AVATAR_CONFIG.ERRORS.NO_USERNAME);
  }

  const croppedImage = await getCroppedImage(selectedFile, croppedAreaPixels);

  const renamedFile = new File([croppedImage], user?.login ?? "", {
    type: croppedImage.type,
    lastModified: Date.now(),
  });

  return renamedFile;
}
